import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

import { UseOutletContextExpenseReport } from './ExpenseReport';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ExpenseReportDA } from '../../../../services/admin/expense.report.service';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Error } from '../../../../models/error.model';
import { legendInvalid } from '../../../../utils/legend.data.util';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import ExpenseReportFormHeader from './ExpenseReportFormHeader';
import AppPreloader from '../../../../components/Preloader';

export interface ExpenseReportCreatePageProps {};

let errorResponse: Error;

const ExpenseReportCreatePage: React.FunctionComponent<ExpenseReportCreatePageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany, productsForCompany, servicesForCompany} = UseOutletContextExpenseReport()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [employee, setEmployee] = useState({value: '', valid: false, object: null as any})
  const [concerning, setConcerning] = useState({value: '', valid: false})

  const submitCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && employee.valid && concerning.valid) {
      const companyData = {uuid: companyForUser.uuid, name: companyForUser.name, logo: companyForUser.logo, address: companyForUser.address, phone: companyForUser.phone || '', email: companyForUser.email || '', website: companyForUser.website || '', vat_number: companyForUser.vat_number || ''}
      const dateToday = format(new Date(), 'yyyy-MM-dd')

      try {
        const reportResponse = await ExpenseReportDA.postExpenseReport(companyForUser.id, employee.value, concerning.value, dateToday, companyForUser.currency, null, [], companyData)

        if (reportResponse.status !== 201 && reportResponse.status !== 200) {
          errorResponse = reportResponse.data
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
          setLoadIndicator('off')
          return
        }

        const report = reportResponse.data
        const reportId = report?.id

        if (!report?.id_contact) {
          GlobalSwal.fire({
            title: getError('EMPLOYEE_NO_CONTACT', lang.code, 'Employee is not linked to a payroll contact'),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
          setLoadIndicator('off')
          return
        }

        navigate(`/main/purchase/expense-report/update/${reportId}`, {replace: true})
      } catch (error) {
        errorWebHandler(lang, error)
      } finally {
        setLoadIndicator('off')
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        if (!employee.valid) legendInvalid('legend-employee-required')
        if (!concerning.value) legendInvalid('legend-concerning-required')
        setLoadIndicator('off')
      })
    }
  }

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.expenseReports, branch: lang.labels.createExpenseReport}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && productsForCompany && servicesForCompany
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="form">
              <ExpenseReportFormHeader mode="create" employee={employee} concerning={concerning} setEmployee={setEmployee} setConcerning={setConcerning}></ExpenseReportFormHeader>
              <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
                <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
                  <span className="svg-icon svg-icon-3tx svg-icon-primary me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                      <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                    </svg>
                  </span>
                  <div className="d-grid">
                    <span className="text-start text-dark fw-bolder fs-5">{lang.labels.processOfCreatingANewExpenseReportCompleted}</span>
                    <span className="text-justify text-muted fw-bold">
                      {lang.labels.clickOn}
                      <span className="text-primary text-lowercase fw-bolder mx-1">{lang.labels.createExpenseReport}</span>
                      {lang.labels.ifYouWantToSaveTheNewExpenseReport}
                    </span>
                  </div>
                </div>
                <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitCreate}>
                  <span className="indicator-label text-uppercase text-nowrap">{lang.labels.createExpenseReport}</span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ExpenseReportCreatePage;
